<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_PLAYERS } from '@/services/mockData';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NProgress, NTag, NInput, NSelect } from 'naive-ui';
import {
  Dumbbell,
  CheckCircle2,
  Calendar,
  Flame,
  Award,
  UploadCloud,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Target,
  Plus,
  Activity,
  Zap,
  BarChart3
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant, currentUser } = useTenant();
const { notifySuccess, notifyInfo } = useToastNotification();

const athlete = computed(() => INITIAL_PLAYERS[0]); // Cole Vance #24

// Weekly Training Homework Checklist
const homeworkDrills = ref([
  {
    id: 'dr_1',
    title: 'High-Velo Plyo Ball Arm Pathway (Green & Blue)',
    sets: '3 sets x 8 reps',
    target: 'Rotator Cuff & Deceleration',
    completed: true,
  },
  {
    id: 'dr_2',
    title: 'Front Toss Top-Hand Extension Isolation',
    sets: '40 swings',
    target: 'Opposite-Field Barrel Control',
    completed: true,
  },
  {
    id: 'dr_3',
    title: 'Shortstop Backhand Crossover & Quick Transfer',
    sets: '25 grounders',
    target: 'Footwork & 0.62s Pop Time',
    completed: false,
  },
  {
    id: 'dr_4',
    title: 'Post-Throwing Band Care & Shoulder Mobility Routine',
    sets: '15 minutes',
    target: 'Injury Prevention & Recovery',
    completed: false,
  },
]);

// Bullpen & Throwing Sessions History
const bullpenSessions = ref([
  {
    id: 'bp_1',
    date: '2025-03-04',
    pitches: 35,
    strikePct: 71,
    maxVelo: 94.2,
    avgVelo: 91.8,
    pitchMix: '4-Seam (60%), Slider (25%), Changeup (15%)',
    notes: 'Sharp bite on slider. Maintained arm slot throughout all 35 pitches.',
  },
  {
    id: 'bp_2',
    date: '2025-02-28',
    pitches: 40,
    strikePct: 68,
    maxVelo: 93.6,
    avgVelo: 90.5,
    pitchMix: '4-Seam (50%), Curve (30%), Changeup (20%)',
    notes: 'Good depth on curveball. First pitch strike rate at 74%.',
  },
]);

// Modals
const isAddDrillOpen = ref(false);
const isLogBullpenOpen = ref(false);

// New Drill Form
const newDrill = ref({
  title: '',
  sets: '',
  target: '',
});

// New Bullpen Form
const newBullpen = ref({
  pitches: 30,
  strikePct: 70,
  maxVelo: 93.0,
  avgVelo: 90.5,
  pitchMix: '4-Seam Fastball & Sweeper',
  notes: '',
});

const toggleDrill = (drill: typeof homeworkDrills.value[0]) => {
  drill.completed = !drill.completed;
  if (drill.completed) {
    notifySuccess(t('training.drillCompleted'), drill.title);
  }
};

const handleCreateDrill = () => {
  if (!newDrill.value.title) return;
  homeworkDrills.value.push({
    id: `dr_${Date.now()}`,
    title: newDrill.value.title,
    sets: newDrill.value.sets || '3 sets x 10 reps',
    target: newDrill.value.target || 'General Mechanics',
    completed: false,
  });
  isAddDrillOpen.value = false;
  newDrill.value = { title: '', sets: '', target: '' };
  notifySuccess('Drill Added', 'New training drill assigned to weekly program.');
};

const handleLogBullpen = () => {
  bullpenSessions.value.unshift({
    id: `bp_${Date.now()}`,
    date: new Date().toISOString().split('T')[0],
    pitches: Number(newBullpen.value.pitches),
    strikePct: Number(newBullpen.value.strikePct),
    maxVelo: Number(newBullpen.value.maxVelo),
    avgVelo: Number(newBullpen.value.avgVelo),
    pitchMix: newBullpen.value.pitchMix,
    notes: newBullpen.value.notes || 'Solid bullpen command session.',
  });
  isLogBullpenOpen.value = false;
  notifySuccess('Bullpen Logged', `${newBullpen.value.pitches} pitches recorded with ${newBullpen.value.maxVelo} MPH peak.`);
};

const completedCount = computed(() => homeworkDrills.value.filter(d => d.completed).length);
const progressPct = computed(() => homeworkDrills.value.length > 0 ? Math.round((completedCount.value / homeworkDrills.value.length) * 100) : 0);

const handleUploadVideoHomework = () => {
  notifySuccess(t('training.homeworkUploaded'), t('training.homeworkUploadedMsg'));
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('training.title') }}
          </h1>
          <AppBadge variant="success">Biomechanics Active</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('training.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-2.5">
        <NButton
          secondary
          @click="isLogBullpenOpen = true"
          class="font-semibold text-xs"
        >
          <template #icon><Activity class="w-3.5 h-3.5 text-sky-400" /></template>
          Log Bullpen
        </NButton>

        <NButton
          type="primary"
          @click="handleUploadVideoHomework"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon><UploadCloud class="w-4 h-4" /></template>
          {{ t('training.uploadDrillVideo') }}
        </NButton>
      </div>
    </div>

    <!-- Progress Overview Banner -->
    <div class="p-6 rounded-3xl bg-gradient-to-r from-emerald-950 via-slate-900 to-indigo-950 border border-emerald-500/20 shadow-2xl">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div class="flex items-center gap-4">
          <div class="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center font-black text-xl">
            <Dumbbell class="w-7 h-7" />
          </div>
          <div>
            <h2 class="text-lg font-black text-white">{{ t('training.weeklyProgramTitle') }}</h2>
            <p class="text-xs text-slate-300 mt-0.5">
              Assigned by Coach Holloway &bull; Focus: <strong class="text-emerald-400">Exit Velocity & Lateral First Step</strong>
            </p>
          </div>
        </div>

        <div class="w-full md:w-64 space-y-1.5">
          <div class="flex items-center justify-between text-xs font-bold text-slate-300">
            <span>{{ t('training.weekProgress') }}</span>
            <span class="font-mono text-emerald-400">{{ progressPct }}%</span>
          </div>
          <NProgress type="line" :percentage="progressPct" status="success" :show-indicator="false" />
        </div>
      </div>
    </div>

    <!-- Main Grid: Homework Drills & Velocity Metric Progress -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- Drill Assignments Checklist (7 cols) -->
      <div class="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
        <div class="flex items-center justify-between pb-3 border-b border-slate-800">
          <div class="flex items-center gap-2">
            <Target class="w-4 h-4 text-sky-400" />
            <h3 class="text-sm font-bold uppercase tracking-wider text-slate-100">{{ t('training.assignedDrills') }}</h3>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-slate-400 font-semibold">{{ completedCount }} / {{ homeworkDrills.length }} {{ t('training.done') }}</span>
            <button
              type="button"
              @click="isAddDrillOpen = true"
              class="p-1 rounded-lg text-sky-400 hover:bg-sky-500/10 transition"
              title="Add Custom Drill"
            >
              <Plus class="w-4 h-4" />
            </button>
          </div>
        </div>

        <div class="space-y-3">
          <div
            v-for="drill in homeworkDrills"
            :key="drill.id"
            @click="toggleDrill(drill)"
            :class="[
              'p-4 rounded-2xl border transition cursor-pointer flex items-start justify-between gap-4',
              drill.completed
                ? 'bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-500/20'
                : 'bg-slate-950 border-slate-800 hover:border-slate-700'
            ]"
          >
            <div class="flex items-start gap-3">
              <div
                :class="[
                  'w-6 h-6 rounded-lg flex items-center justify-center text-xs mt-0.5 transition',
                  drill.completed ? 'bg-emerald-500 text-slate-950' : 'border border-slate-700 text-transparent'
                ]"
              >
                <CheckCircle2 class="w-4 h-4" />
              </div>

              <div>
                <h4 :class="['text-xs font-bold transition', drill.completed ? 'line-through text-slate-400' : 'text-slate-100']">
                  {{ drill.title }}
                </h4>
                <div class="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-400">
                  <span class="font-mono text-sky-400 font-semibold">{{ drill.sets }}</span>
                  <span>&bull;</span>
                  <span>Target: {{ drill.target }}</span>
                </div>
              </div>
            </div>

            <AppBadge :variant="drill.completed ? 'success' : 'slate'">
              {{ drill.completed ? 'Done' : 'Pending' }}
            </AppBadge>
          </div>
        </div>

        <!-- Bullpen Session Log History List -->
        <div class="pt-4 border-t border-slate-800 space-y-3">
          <div class="flex items-center justify-between">
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <BarChart3 class="w-4 h-4 text-emerald-400" />
              Recent Bullpen Sessions
            </h4>
            <span class="text-[11px] text-slate-500 font-mono">{{ bullpenSessions.length }} logs</span>
          </div>

          <div class="space-y-2.5">
            <div
              v-for="bp in bullpenSessions"
              :key="bp.id"
              class="p-3 bg-slate-950 rounded-2xl border border-slate-800 text-xs space-y-1.5"
            >
              <div class="flex items-center justify-between">
                <span class="font-bold text-slate-200">{{ bp.date }} &bull; {{ bp.pitches }} Pitches</span>
                <span class="font-mono font-bold text-emerald-400">{{ bp.strikePct }}% Strikes &bull; {{ bp.maxVelo }} Max MPH</span>
              </div>
              <p class="text-slate-400 text-[11px]">{{ bp.pitchMix }}</p>
              <p class="text-slate-500 text-[11px] italic">"{{ bp.notes }}"</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Velo Progression & Bullpen Log (5 cols) -->
      <div class="lg:col-span-5 space-y-4">
        <!-- Exit Velocity / Throwing Metric Trend -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="text-xs font-bold uppercase tracking-wider text-slate-200 flex items-center gap-1.5">
              <TrendingUp class="w-4 h-4 text-emerald-400" />
              Velocity Progression
            </h3>
            <NTag type="success" size="small" round :bordered="false">+4.2 MPH Season</NTag>
          </div>

          <div class="space-y-3">
            <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between text-xs">
              <div>
                <span class="text-slate-400 block">Peak Exit Velocity (Off Tee)</span>
                <span class="font-bold text-slate-100 text-sm font-mono mt-0.5 block">94.2 MPH</span>
              </div>
              <span class="text-emerald-400 font-bold font-mono">Top 5%</span>
            </div>

            <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between text-xs">
              <div>
                <span class="text-slate-400 block">Infield Throwing Velo (SS to 1B)</span>
                <span class="font-bold text-slate-100 text-sm font-mono mt-0.5 block">89.6 MPH</span>
              </div>
              <span class="text-sky-400 font-bold font-mono">D1 Caliber</span>
            </div>
          </div>
        </div>

        <!-- Coach Feedback Bulletin -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-2.5 text-xs text-slate-300">
          <span class="text-[10px] uppercase font-bold text-amber-400 tracking-wider block">Coach Review Note</span>
          <p class="leading-relaxed text-slate-200">
            "Cole: great extension on opposite-field drills yesterday. Keep your rear heel planted a split second longer before torso rotation."
          </p>
          <span class="text-[10px] text-slate-500 block font-semibold">— Coach Marcus Holloway (Yesterday @ 18:20)</span>
        </div>
      </div>
    </div>

    <!-- Modal: Add Custom Drill -->
    <AppModal
      v-model="isAddDrillOpen"
      title="Add Custom Training Drill"
      subtitle="Assign a new training item to the development plan"
      max-width="md"
    >
      <form @submit.prevent="handleCreateDrill" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">Drill Name</label>
          <NInput v-model:value="newDrill.title" placeholder="e.g. 5-Cone Lateral Agility & Throw" required />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">Sets / Reps / Duration</label>
            <NInput v-model:value="newDrill.sets" placeholder="e.g. 4 sets x 6 reps" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">Development Target</label>
            <NInput v-model:value="newDrill.target" placeholder="e.g. First-Step Quickness" />
          </div>
        </div>

        <div class="pt-3 flex justify-end gap-2 border-t border-slate-800">
          <NButton quaternary @click="isAddDrillOpen = false">Cancel</NButton>
          <NButton type="primary" attr-type="submit" class="font-bold text-xs">Save Drill</NButton>
        </div>
      </form>
    </AppModal>

    <!-- Modal: Log Bullpen Session -->
    <AppModal
      v-model="isLogBullpenOpen"
      title="Log Bullpen Throwing Session"
      subtitle="Track bullpen metrics, pitch counts, and pitch arsenal"
      max-width="md"
    >
      <form @submit.prevent="handleLogBullpen" class="space-y-4">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">Total Pitches Thrown</label>
            <input
              v-model.number="newBullpen.pitches"
              type="number"
              min="1"
              max="120"
              required
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">Strike Percentage (%)</label>
            <input
              v-model.number="newBullpen.strikePct"
              type="number"
              min="0"
              max="100"
              required
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">Max Velocity (MPH)</label>
            <input
              v-model.number="newBullpen.maxVelo"
              type="number"
              step="0.1"
              required
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">Avg Velocity (MPH)</label>
            <input
              v-model.number="newBullpen.avgVelo"
              type="number"
              step="0.1"
              required
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100"
            />
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">Pitch Mix Breakdown</label>
          <NInput v-model:value="newBullpen.pitchMix" placeholder="e.g. 4-Seam (60%), Slider (30%), Changeup (10%)" />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">Coach / Athlete Notes</label>
          <NInput v-model:value="newBullpen.notes" type="textarea" :rows="2" placeholder="Feel, mechanics, arm recovery notes..." />
        </div>

        <div class="pt-3 flex justify-end gap-2 border-t border-slate-800">
          <NButton quaternary @click="isLogBullpenOpen = false">Cancel</NButton>
          <NButton type="primary" attr-type="submit" class="font-bold text-xs">Save Session</NButton>
        </div>
      </form>
    </AppModal>
  </div>
</template>